import { useState } from "react";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "creative-adapt-cookie-notice-dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return true;
  }
}

/**
 * Minimal informational cookie notice. The app only sets a strictly
 * necessary session cookie (no tracking/advertising cookies), so no
 * consent choice is required — this banner informs and links to /policy.
 */
export function CookieNotice() {
  const { t } = useTranslation("common");
  const [dismissed, setDismissed] = useState(readDismissed);

  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore storage failures; the banner just reappears next load
    }
    setDismissed(true);
  };

  return (
    <div
      data-testid="banner-cookie-notice"
      role="region"
      aria-label={t("cookieNotice.text")}
      className="fixed inset-x-0 bottom-0 z-[90] flex justify-center px-4 pb-4"
      style={{ pointerEvents: "none" }}
    >
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-2"
        style={{
          pointerEvents: "auto",
          maxWidth: 720,
          background: "rgba(28,22,17,.96)",
          border: "1px solid rgba(255,255,255,.14)",
          borderRadius: 16,
          boxShadow: "0 18px 40px rgba(0,0,0,.5)",
          padding: "14px 18px",
        }}
      >
        <p style={{ flex: "1 1 320px", fontSize: 13, lineHeight: 1.55, color: "#C0B2A4", margin: 0 }}>
          {t("cookieNotice.text")}{" "}
          <Link
            href="/policy"
            data-testid="link-cookie-policy"
            style={{ color: "#FF8A2B", textDecoration: "underline" }}
          >
            {t("cookieNotice.learnMore")}
          </Link>
        </p>
        <button
          type="button"
          data-testid="button-cookie-dismiss"
          onClick={dismiss}
          className="transition-[filter] hover:brightness-110"
          style={{
            padding: "9px 16px",
            border: "none",
            borderRadius: 11,
            background: "linear-gradient(135deg,#A855F7,#FF5900)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          {t("cookieNotice.dismiss")}
        </button>
      </div>
    </div>
  );
}
